import {GetInPlaceRentSetupQuery, OriginalRevenueUnitTypeNumbersModel} from "../../../../__generated__/generated_types";
import {GridSettings} from "handsontable/settings";
import "native-injects";
import {Property} from "../../../../contexts/properties/PropertiesContext";
import {formatterIntNoGrouping} from "../../../../utils/formatters";
import {MONTHS} from "../../../../constants/Months";
import HotTable from "@handsontable/react";
import {CellValue} from "handsontable/common";
import { getMonths } from "../../../../utils/months-helper";

export type HOTData = {
    tableData: Required<GridSettings["data"]>;
    rowNumToUnitTypeId: Record<number, string>;
    calcOverridesByUnitTypeId: Record<string, (number | null | undefined)[]>;
    originalByUnitTypeId: Record<string, (number | null | undefined)[]>;
    actualOverridesByUnitTypeId: Record<string, (number | null | undefined)>;
    pmsValuesByUnitTypeId: Record<string, (string | null)[]>;
    colHeaders: string[];
};

export type PendingUpdates = {
    unitTypeUpdates: Map<string, (number | null | undefined)[]>; // unit type id -> array with length = total months (25 - reforecastStartMonthIndex)
};

function formatVal(val: number | null | undefined): string {
    let formatted = "";
    if(val !== null && val !== undefined) {
        formatted = formatterIntNoGrouping.format(val);
    }

    return formatted;
}

function formatOriginalVal(originalVal: number | null | undefined): number | null | undefined {
    if(originalVal === null || originalVal === undefined) {
        return originalVal;
    }
    return +formatterIntNoGrouping.format(originalVal);
}

export function calculateTotalMonths(property: Property): number {
    return 25 - property.reforecastStartMonthIndex;
}

function getActualValuePair(data: GetInPlaceRentSetupQuery["queryUnitTypesPMSActualsWithOverridesYear"][0], monthIndex: number): {
    valueOrOverride: number | null | undefined,
    originalValue: number | null | undefined;
} {
    return {
        valueOrOverride: data.inPlaceRent.overrides[monthIndex] != null ?
            data.inPlaceRent.overrides[monthIndex]
            : data.inPlaceRent.originals[monthIndex],
        originalValue: data.inPlaceRent.originals[monthIndex]
    };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function calculateActualsYearAndMonth(property: Property) {
    let actualsMonthIndex = property.reforecastStartMonthIndex - 1;
    let actualsYear = property.reforecastYear;
    if(actualsMonthIndex < 0) {
        actualsMonthIndex = 11;
        actualsYear = property.reforecastYear - 1;
    }

    return {actualsMonthIndex, actualsYear};
}

export function buildHOTDataV2(property: Property, rawData: GetInPlaceRentSetupQuery): HOTData {
    const {actualsMonthIndex, actualsYear} = calculateActualsYearAndMonth(property);
    const months = getMonths(property);

    const COLHEADERS: string[] = ["Unit Type"];
    COLHEADERS.push(`
        <span>${property.isAcquisitionProperty ? "T12" : "ACTUAL"}</span>
        <span>${property.isAcquisitionProperty ? "" : actualsYear % 100}</span>
        <span>${months[actualsMonthIndex]}</span>
    `);
    for(let i = property.reforecastStartMonthIndex; i < 12; i++) {
        COLHEADERS.push(`
            <span>${property.isAcquisitionProperty ? "BDGT" : "RFCST"}</span>
            <span>${property.isAcquisitionProperty ? "Y1" : (i == property.reforecastStartMonthIndex ? property.reforecastYear % 100 : "")}</span>
            <span>${months[i]}</span>
        `);
    }
    if(!property.isAcquisitionProperty){
        for (let i = 0; i < 12; i++) {
            COLHEADERS.push(`
                <span>BDGT</span>
                <span>${i == 0 ? property.reforecastYear % 100 + 1 : `&nbsp;`}</span>
                <span>${months[i]}</span>
            `);
        }
    }
    const tableData = [];
    tableData.push(COLHEADERS);

    const calculationOverridesByUnitTypeId = rawData.originalRevenueInPlaceRentOverrides.toIdMap("unitTypeId");
    const calcOverridesByUnitTypeIdFlat = rawData.originalRevenueInPlaceRentOverrides.toIdMap("unitTypeId", row => [null, ...row.reforecastOverrides.slice(property.reforecastStartMonthIndex), ...row.budgetOverrides]);
    const pmsValuesByUnitTypeIdFlat = {} as Record<string, (string | null)[]>;
    let calculatedByUnitTypeIdRaw: Record<string, Pick<OriginalRevenueUnitTypeNumbersModel, "unitTypeId" | "reforecast" | "budget">> = {};
    const originalByUnitTypeId: Record<string, (number | null | undefined)[]> = {};
    if(rawData.originalRevenueModel) {
        calculatedByUnitTypeIdRaw = rawData.originalRevenueModel.unitTypeInPlaceRent.toIdMap("unitTypeId");
    }
    const pmsValuesByUnitTypeId = rawData.queryPMSInPlaceRentsForPropertyAndYears.groupBy("unitTypeId");
    const actualOverridesByUnitTypeId = rawData.queryUnitTypesPMSActualsWithOverridesYear.toIdMap("unitTypeId", row => row.inPlaceRent.overrides[actualsMonthIndex]);
    const rowNumToUnitTypeId: Record<number, string> = {};

    for(const unitTypeActualsWithOverrides of rawData.queryUnitTypesPMSActualsWithOverridesYear.sortBy("unitTypeName")) {
        const currentValuesRow = [];
        const originals = [];

        const actualValuePair = getActualValuePair(unitTypeActualsWithOverrides, actualsMonthIndex);
        currentValuesRow.push(formatVal(actualValuePair.valueOrOverride));
        originals.push(formatOriginalVal(actualValuePair.originalValue));


        const calculated = calculatedByUnitTypeIdRaw[unitTypeActualsWithOverrides.unitTypeId];
        const calcOverride = calculationOverridesByUnitTypeId[unitTypeActualsWithOverrides.unitTypeId];
        const lastIdx = property.isAcquisitionProperty ? 12 : 24;
        for(let i = property.reforecastStartMonthIndex; i < lastIdx; i++) {
            let val = null;
            let original = null;
            if(i < 12) {
                val = calcOverride?.reforecastOverrides[i];
                original = calculated?.reforecast[i];
                if(val == null) {
                    val = calculated?.reforecast[i];
                }
            }
            else {
                val = calcOverride?.budgetOverrides[i - 12];
                original = calculated?.budget[i - 12];
                if(val == null) {
                    val = calculated?.budget[i - 12];
                }
            }
            currentValuesRow.push(formatVal(val));
            originals.push(formatOriginalVal(original));
        }
        tableData.push([
            unitTypeActualsWithOverrides.unitTypeName,
            ...currentValuesRow,
        ]);
        rowNumToUnitTypeId[tableData.length - 1] = unitTypeActualsWithOverrides.unitTypeId;

        let pmsValuesForActual = [null] as (string | null)[];
        let pmsValuesForRfcst = new Array(12 - property.reforecastStartMonthIndex).fill(null);
        let pmsValuesForBdgt = new Array(12).fill(null);
        const pmsValuesForUnitType = pmsValuesByUnitTypeId[unitTypeActualsWithOverrides.unitTypeId];
        if(pmsValuesForUnitType) {
            const actualMonthsValues = pmsValuesForUnitType.find(row => row.year == actualsYear);
            if(actualMonthsValues) {
                const val = actualMonthsValues.values[actualsMonthIndex];
                pmsValuesForActual = [val == null ? null : formatVal(val)];
            }
            const rfcstMonthsValues = pmsValuesForUnitType.find(row => row.year == property.reforecastYear);
            if(rfcstMonthsValues) {
                pmsValuesForRfcst = rfcstMonthsValues.values.slice(property.reforecastStartMonthIndex).map(val => val == null ? null : formatVal(val));
            }
            const bdgtMonthsValues = pmsValuesForUnitType.find(row => row.year == property.budgetYear);
            if(bdgtMonthsValues) {
                pmsValuesForBdgt = bdgtMonthsValues.values.map(val => val == null ? null : formatVal(val));
            }
        }

        const pmsValues = [
            "     PMS Values",
            ...pmsValuesForActual,
            ...pmsValuesForRfcst
        ];
        if(!property.isAcquisitionProperty) {
            pmsValues.push(...pmsValuesForBdgt);
        }
        tableData.push(pmsValues);
        pmsValuesByUnitTypeIdFlat[unitTypeActualsWithOverrides.unitTypeId] = [...pmsValuesForActual, ...pmsValuesForRfcst, ...pmsValuesForBdgt];
        originalByUnitTypeId[unitTypeActualsWithOverrides.unitTypeId] = originals;
    }

    return {
        tableData,
        rowNumToUnitTypeId,
        colHeaders: COLHEADERS,
        calcOverridesByUnitTypeId: calcOverridesByUnitTypeIdFlat,
        originalByUnitTypeId: originalByUnitTypeId,
        actualOverridesByUnitTypeId,
        pmsValuesByUnitTypeId: pmsValuesByUnitTypeIdFlat
    };
}

export function buildClearOverridesUpdates(
    property: Property,
    hotRef: React.RefObject<HotTable>,
    rowToUnitTypeIdMap: Record<number, string>,
    calcOverridesByUnitTypeId: Record<string, (number | null | undefined)[]>,
    actualOverridesByUnitTypeId: Record<string, (number | null | undefined)>,
    originalByUnitTypeId: Record<string, (number | null | undefined)[]>
): {updates: Map<string, (number | null | undefined | "unset")[]>, changes: Array<[number, number, CellValue]>;} {
    const totalMonths = calculateTotalMonths(property);
    const hot = hotRef.current?.hotInstance;
    const updates: Map<string, (number | null | undefined | "unset")[]> = new Map();
    const changes: Array<[number, number, CellValue]> = [];
    if(!hot) {
        return {updates, changes};
    }

    const selectedRange = hot.getSelectedRange()?.flatMap(range => range.getAll()) ?? [];
    for(const {row, col} of selectedRange) {
        const unitTypeId = rowToUnitTypeIdMap[row];
        if(!unitTypeId) {
            continue;
        }
        let overrideVal = undefined;
        if(col - 1 === 0) {
            overrideVal = actualOverridesByUnitTypeId[unitTypeId];
        }
        if(col - 1 > 0 && col - 1 < totalMonths) {
            overrideVal = calcOverridesByUnitTypeId[unitTypeId]?.[col - 1];
        }
        let utUpdate = updates.get(unitTypeId);
        if(!utUpdate) {
            utUpdate = new Array(totalMonths).fill(undefined);
            updates.set(unitTypeId, utUpdate);
        }
        if(overrideVal == undefined || overrideVal == null) {
            utUpdate[col - 1] = "unset";
            changes.push([row, col, originalByUnitTypeId[unitTypeId]?.[col - 1]]);
        }
        else {
            utUpdate[col - 1] = null;
        }
    }

    return {updates, changes};
}

export function mergeUpdates(base: PendingUpdates["unitTypeUpdates"] | undefined, updateWith: Map<string, (number | null | undefined | "unset")[]>): PendingUpdates["unitTypeUpdates"] {
    const unitTypeUpdates: PendingUpdates["unitTypeUpdates"] = base ? new Map(base) : new Map();
    for(const [unitTypeId, updateRow] of Array.from(updateWith)) {
        let resultRow = unitTypeUpdates.get(unitTypeId);
        if(!resultRow) {
            resultRow = new Array(updateRow.length).fill(undefined);
            unitTypeUpdates.set(unitTypeId, resultRow);
        }
        for(let i = 0; i < updateRow.length; i++) {
            const updateVal = updateRow[i];
            if(updateVal !== undefined) {
                if(updateVal === "unset") {
                    resultRow[i] = undefined;
                }
                else {
                    resultRow[i] = updateVal;
                }
            }
        }
    }
    return unitTypeUpdates;
}

export function buildMonthlyOverrideValidity(
    updates: PendingUpdates["unitTypeUpdates"],
    calcOverridesByUnitTypeId: HOTData["calcOverridesByUnitTypeId"],
    property: Property
): Array<boolean> {
    const totalMonths = calculateTotalMonths(property);
    const invalidMonths = new Array<boolean>(totalMonths).fill(false);
    for(let i = 1; i < totalMonths; i++) {
        const monthValues = new Set<number | null>();
        for(const [unitTypeId, calcOverrides] of Object.entries(calcOverridesByUnitTypeId)) {
            let overrideVal = calcOverrides[i];
            if(overrideVal === undefined) {
                overrideVal = null;
            }
            else if(overrideVal !== null) {
                overrideVal = 0; // need a distinction between number and null. all numbers should be treated as just a number without differentiating their values
            }
            const utUpdate = updates.get(unitTypeId);
            if(utUpdate) {
                const utUpdateVal = utUpdate[i];
                if(utUpdateVal === null) {
                    overrideVal = utUpdateVal;
                }
                else if(utUpdateVal !== undefined) {
                    overrideVal = 0; // again - the fact itself that the val is number is all that matters
                }
            }
            monthValues.add(overrideVal);
        }
        invalidMonths[i] = monthValues.size > 1;
    }
    return invalidMonths;
}

export function buildPreservePMSValueUpdates(
    hotRef: React.RefObject<HotTable>,
    rowToUnitTypeIdMap: Record<number, string>,
    pmsValuesByUnitTypeId: Record<string, (string | null)[]>,
    property: Property
): {updates: PendingUpdates["unitTypeUpdates"], changes: Array<[number, number, CellValue]>;} {
    const totalMonths = calculateTotalMonths(property);
    const hot = hotRef.current?.hotInstance;
    const updates: PendingUpdates["unitTypeUpdates"] = new Map();
    if(!hot) {
        return {updates, changes: []};
    }

    const selectedRange = hot.getSelectedRange()?.flatMap(range => range.getAll()) ?? [];
    const changes: Array<[number, number, CellValue]> = [];
    for(const {row, col} of selectedRange) {
        const unitTypeId = rowToUnitTypeIdMap[row];
        if(!unitTypeId) {
            continue;
        }
        let overrideVal = undefined;
        if(col - 1 >= 0 && col - 1 < totalMonths) {
            overrideVal = pmsValuesByUnitTypeId[unitTypeId]?.[col - 1];
        }
        if(overrideVal === undefined || overrideVal === null) {
            continue;
        }
        let utUpdate = updates.get(unitTypeId);
        if(!utUpdate) {
            utUpdate = new Array(totalMonths).fill(undefined);
            updates.set(unitTypeId, utUpdate);
        }
        utUpdate[col - 1] = +overrideVal;
        changes.push([row, col, overrideVal]);
    }
    return {updates, changes};
}